import React from "react"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { LevelBadge, Btn, Badge } from "../shared/components"
import { chat as aiChat, rateMessage, listConversations, getMessages } from "../api/ai.api"
import { listKBDocs } from "../api/kb.api"
import toast from "react-hot-toast"

/* ============================================================
   AI Assistant Chat
   ============================================================ */

/* Mode jawaban — kunci harus sama persis dengan MODE_INSTRUCTIONS di
   backend services/ai.service.js; backend menolak nilai di luar daftar itu. */
const ANSWER_MODES = [
  { key: "STANDARD",   id: "Standar",                  en: "Standard" },
  { key: "BEGINNER",   id: "Jelaskan seperti pemula",  en: "Explain like a beginner" },
  { key: "TECHNICAL",  id: "Detail teknis",            en: "Technical detail" },
  { key: "QUIZ_COACH", id: "Quiz coach",               en: "Quiz coach" },
  { key: "SCENARIO",   id: "Skenario simulasi",        en: "Simulated scenario" },
]

/* Kelompokkan percakapan per hari supaya judul Hari ini / Kemarin memang
   mencerminkan tanggalnya, bukan sekadar label tetap. */
function groupByDay(list) {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1)
  const groups = { today: [], yesterday: [], older: [] }
  for (const c of list) {
    const d = new Date(c.created_at)
    if (d >= today) groups.today.push(c)
    else if (d >= yesterday) groups.yesterday.push(c)
    else groups.older.push(c)
  }
  return groups
}

/* Bentuk pesan dari server -> bentuk yang dipakai daftar percakapan di layar. */
function toConvMessage(m) {
  if (m.role === "USER") return { role: "user", content: m.content }
  const sources = Array.isArray(m.sources) ? m.sources : []
  return {
    role: "assistant",
    messageId: m.id,
    content: m.content,
    // `sources` tetap dipakai — bukan untuk didaftar satu per satu, melainkan
    // untuk menentukan apakah jawaban punya dasar di Knowledge Base.
    conf: sources.length ? "high" : "none",
    notFound: !sources.length,
    feedback: m.feedback ?? null,
  }
}

function AIChatScreen({ compact = false }) {
  const t = useT();
  const { lang, role, navigate, currentProgramId, currentProgram, user } = useApp();
  const isMobile = useIsMobile();
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [conversationId, setConversationId] = React.useState(null);
  // null = still loading; the context panel distinguishes that from "empty".
  const [kbDocs, setKbDocs] = React.useState(null);
  // Riwayat percakapan milik pengguna ini. null = masih dimuat.
  const [conversations, setConversations] = React.useState(null);
  const [loadingConv, setLoadingConv] = React.useState(false);
  const [mode, setMode] = React.useState("STANDARD");

  const systemLine = React.useCallback(() => ({
    role: "system",
    content: lang === "id"
      ? `Konteks: ${currentProgram?.name || "—"} · Knowledge Base aktif`
      : `Context: ${currentProgram?.name || "—"} · Active Knowledge Base`,
  }), [lang, currentProgram?.name])

  const [conv, setConv] = React.useState(() => [{
    role: "system",
    content: lang === "id"
      ? `Konteks: ${currentProgram?.name || "—"} · Knowledge Base aktif`
      : `Context: ${currentProgram?.name || "—"} · Active Knowledge Base`,
  }])

  // Riwayat percakapan nyata milik pengguna, dari server. Endpoint ini sudah
  // lama ada; sebelumnya layar ini merender daftar contoh yang sama untuk
  // semua orang, seolah percakapan mereka sendiri.
  const refreshConversations = React.useCallback(() => {
    if (!currentProgramId) { setConversations([]); return Promise.resolve() }
    return listConversations(currentProgramId)
      .then((d) => setConversations(Array.isArray(d) ? d : []))
      // Non-fatal: daftar riwayat adalah pelengkap, bukan chat itu sendiri.
      .catch(() => setConversations([]))
  }, [currentProgramId])

  React.useEffect(() => {
    let cancelled = false
    setConversations(null)
    if (!currentProgramId) { setConversations([]); return }
    listConversations(currentProgramId)
      .then((d) => { if (!cancelled) setConversations(Array.isArray(d) ? d : []) })
      .catch(() => { if (!cancelled) setConversations([]) })
    return () => { cancelled = true }
  }, [currentProgramId])

  async function openConversation(id) {
    if (id === conversationId || loadingConv) return
    setLoadingConv(true)
    try {
      const msgs = await getMessages(id)
      setConversationId(id)
      setConv([systemLine(), ...(Array.isArray(msgs) ? msgs.map(toConvMessage) : [])])
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally {
      setLoadingConv(false)
    }
  }

  function startNewConversation() {
    setConversationId(null)
    setConv([systemLine()])
    setInput("")
  }

  React.useEffect(() => {
    if (!currentProgramId) { setKbDocs([]); return }
    let cancelled = false
    setKbDocs(null)
    listKBDocs(currentProgramId)
      .then((d) => { if (!cancelled) setKbDocs(Array.isArray(d) ? d : []) })
      // Non-fatal: the panel is context, not the chat itself.
      .catch(() => { if (!cancelled) setKbDocs([]) })
    return () => { cancelled = true }
  }, [currentProgramId])

  async function handleSend() {
    const text = input.trim();
    if (!text || sending || !currentProgramId) return;
    setSending(true);
    setConv((c) => [...c, { role: "user", content: text }]);
    setInput("");
    try {
      const resp = await aiChat(currentProgramId, text, conversationId, mode);
      const isNew = !conversationId
      if (isNew) setConversationId(resp.conversationId);
      // Percakapan baru harus langsung muncul di daftar riwayat, bukan baru
      // terlihat setelah pindah program atau muat ulang.
      if (isNew) refreshConversations();
      setConv((c) => [
        ...c,
        {
          role: "assistant",
          messageId: resp.messageId,
          content: resp.answer,
          conf: resp.sources?.length ? "high" : "none",
          notFound: !resp.sources?.length,
          feedback: null,
        },
      ]);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  }

  async function handleFeedback(idx, value) {
    const m = conv[idx]
    if (!m || m.role !== "assistant" || !m.messageId) return
    const next = m.feedback === value ? null : value
    // Optimistic update
    setConv((c) => c.map((x, i) => (i === idx ? { ...x, feedback: next } : x)))
    try {
      await rateMessage(m.messageId, next)
    } catch (err) {
      // Roll back on failure
      setConv((c) => c.map((x, i) => (i === idx ? { ...x, feedback: m.feedback } : x)))
      toast.error(err?.response?.data?.error || err.message)
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const suggested = lang === "id" ? [
    "Ringkas dokumen ini",
    "Jelaskan istilah penting di modul ini",
    "Buat 5 soal latihan dari Modul 3",
    "Buat ringkasan poin utama",
  ] : [
    "Summarize this document",
    "Explain the key terms in this module",
    "Generate 5 practice questions from Module 3",
    "Summarise the main points",
  ];

  return (
    <div className={compact ? "" : "main-inner"} style={compact ? { display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" } : {maxWidth: 1280}}>
      <div className="grid" style={{gridTemplateColumns: compact ? "1fr" : (isMobile ? "1fr" : "240px 1fr 280px"), gap: 16, height: compact ? "100%" : "calc(100vh - 100px)", overflow: compact ? "hidden" : undefined}}>
        {/* History sidebar — hidden on mobile and compact mode */}
        {!isMobile && !compact && (
        <div className="card" style={{display:"flex", flexDirection:"column"}}>
          <div style={{padding:"12px 16px", borderBottom:"1px solid var(--border-subtle)"}}>
            <Btn variant="secondary" size="sm" block icon={<Icon name="plus" size={12}/>}
              onClick={startNewConversation} disabled={!conversationId && conv.length <= 1}>
              {lang==="id"?"Percakapan baru":"New conversation"}
            </Btn>
          </div>
          <div style={{flex:1, overflowY:"auto"}}>
            {conversations === null ? (
              <div className="tiny muted" style={{padding:"12px 16px"}}>
                {lang==="id"?"Memuat riwayat…":"Loading history…"}
              </div>
            ) : conversations.length === 0 ? (
              <div className="tiny muted" style={{padding:"12px 16px", lineHeight:1.5}}>
                {lang==="id"
                  ? "Belum ada percakapan. Pertanyaan pertama Anda akan muncul di sini."
                  : "No conversations yet. Your first question will appear here."}
              </div>
            ) : (() => {
              const g = groupByDay(conversations)
              const sections = [
                [t.today, g.today],
                [t.yesterday, g.yesterday],
                [lang==="id"?"SEBELUMNYA":"EARLIER", g.older],
              ]
              return sections.map(([title, items]) => items.length === 0 ? null : (
                <React.Fragment key={title}>
                  <div className="side-section">{title}</div>
                  {items.map((c) => (
                    <button
                      key={c.id}
                      className={"side-item" + (c.id === conversationId ? " active" : "")}
                      style={{padding:"8px 16px", width:"100%", background:"transparent",
                        border:0, cursor:"pointer", textAlign:"left", font:"inherit"}}
                      onClick={() => openConversation(c.id)}
                      title={c.title}
                    >
                      <Icon name="ai" size={14} className="side-icon"/>
                      <span className="small text-truncate">{c.title}</span>
                    </button>
                  ))}
                </React.Fragment>
              ))
            })()}
          </div>
        </div>
        )}

        {/* Main conversation */}
        <div className="card" style={{display:"flex", flexDirection:"column", minHeight: 0}}>
          {/* Header */}
          <div className="card-header">
            <div>
              <div className="row gap-2">
                <Icon name="ai" color="var(--program-color)"/>
                <div className="h3">{t.aiAssistant}</div>
                <Badge tone="brand">RAG</Badge>
              </div>
              <div className="tiny muted mt-1">{lang==="id"?"Konteks:":"Context:"} {currentProgram?.name || "—"}</div>
            </div>
            <div className="row gap-2">
              <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12}/>} onClick={() => setConv([conv[0]])}/>
            </div>
          </div>

          {/* Messages */}
          <div style={{flex:1, overflowY:"auto", padding: isMobile ? "16px" : "24px 32px", minHeight: isMobile ? "55vh" : 0}}>
            {conv.map((m, i) => {
              if (m.role === "system") return (
                <div key={i} className="row gap-2 mb-4" style={{justifyContent:"center"}}>
                  <span className="tag outline tiny">{m.content}</span>
                </div>
              );
              if (m.role === "user") return (
                <div key={i} className="row gap-3 mb-4" style={{justifyContent:"flex-end"}}>
                  <div style={{maxWidth:"70%", padding:"12px 16px", background:"var(--program-color)", color:"white", borderRadius:"var(--r-lg) var(--r-lg) var(--r-xs) var(--r-lg)"}}>
                    {m.content}
                  </div>
                  <div className="avatar" style={{background:"var(--program-color-deep)", flexShrink:0}}>
                    {(user?.fullName || "U").slice(0,2).toUpperCase()}
                  </div>
                </div>
              );
              return (
                <div key={i} className="row gap-3 mb-5" style={{alignItems:"flex-start"}}>
                  <div className="avatar" style={{background:"var(--text)", flexShrink:0}}><Icon name="ai" size={14} color="white"/></div>
                  <div style={{flex:1}}>
                    <div className="row gap-2 mb-2">
                      <strong className="small">AI Assistant</strong>
                      {/* Nama model (mis. "gemini-2.0-flash") sengaja tidak
                          ditampilkan: itu detail penyedia yang tidak berarti
                          bagi peserta, dan menyebut merek pihak ketiga di dalam
                          produk berlabel lembaga justru membingungkan. */}
                      {m.conf === "high" && <span className="tag green tiny"><span className="dot"/>{lang==="id"?"Sumber KB terverifikasi":"KB sources verified"}</span>}
                      {m.conf === "none" && <span className="tag amber tiny"><span className="dot"/>{lang==="id"?"Tidak ditemukan di KB":"Not in KB"}</span>}
                    </div>
                    <div style={{lineHeight:1.6, fontSize:14}}>
                      {m.content.split("\n").map((line, li) => {
                        if (!line) return <br key={li}/>;
                        // Bold parsing
                        const parts = line.split(/\*\*([^*]+)\*\*/);
                        return (
                          <p key={li} style={{margin:"6px 0"}}>
                            {parts.map((p, pi) => pi % 2 === 1 ? <strong key={pi}>{p}</strong> : <span key={pi}>{p}</span>)}
                          </p>
                        );
                      })}
                    </div>
                    {/* Daftar sitasi dihapus atas permintaan: yang tampil adalah
                        nama berkas yang sama berulang kali dengan skor kemiripan
                        mentah (mis. "rel 63%") — angka internal pencarian yang
                        tidak berarti apa-apa bagi peserta, dan memenuhi layar.
                        Sinyal kepercayaan yang benar-benar berguna tetap ada:
                        lencana "Sumber KB terverifikasi" di atas jawaban, dan
                        spanduk "Tidak ditemukan di KB" saat AI tidak menemukan
                        dasarnya. */}
                    {m.notFound && (
                      <div className="row gap-2 mt-3 small" style={{padding:"10px 12px", background:"var(--amber-10)", border:"1px solid var(--amber-50)", borderLeft:"3px solid var(--amber-50)", color:"var(--amber-ink, #6a4f00)"}}>
                        <Icon name="alert" size={14} color="var(--amber-50)"/>
                        <span style={{flex:1}}>{lang==="id"?"Tidak ada hasil di Knowledge Base. AI tidak menebak di luar dokumen.":"No match in the Knowledge Base. The AI does not guess beyond uploaded docs."}</span>
                      </div>
                    )}
                    {/* Actions */}
                    <div className="row gap-1 mt-3">
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="thumb_up" size={12} color={m.feedback === "UP" ? "white" : undefined}/>}
                        disabled={!m.messageId}
                        title={lang==="id"?"Jawaban ini membantu":"This was helpful"}
                        onClick={() => handleFeedback(i, "UP")}
                        style={{
                          padding:"4px 8px",
                          background: m.feedback === "UP" ? "var(--success)" : undefined,
                          color: m.feedback === "UP" ? "white" : undefined,
                        }}
                      />
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="thumb_down" size={12} color={m.feedback === "DOWN" ? "white" : undefined}/>}
                        disabled={!m.messageId}
                        title={lang==="id"?"Jawaban kurang membantu":"This was not helpful"}
                        onClick={() => handleFeedback(i, "DOWN")}
                        style={{
                          padding:"4px 8px",
                          background: m.feedback === "DOWN" ? "var(--danger)" : undefined,
                          color: m.feedback === "DOWN" ? "white" : undefined,
                        }}
                      />
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="copy" size={12}/>}
                        style={{padding:"4px 8px"}}
                        onClick={() => navigator.clipboard?.writeText(m.content || "")}
                        title={lang==="id"?"Salin jawaban":"Copy answer"}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Composer */}
          <div style={{borderTop:"1px solid var(--border)", padding:"16px 20px", background:"var(--g-10)"}}>
            {/* Saran yang menyebut "dokumen ini" hanya masuk akal kalau Knowledge
                Base memang berisi dokumen. Saat kosong, asisten tidak punya apa
                pun untuk diringkas — menawarkannya hanya membuahkan jawaban
                "tidak ditemukan". */}
            {kbDocs && kbDocs.length === 0 ? (
              <div className="tiny muted mb-2" style={{padding:"4px 0", lineHeight:1.5}}>
                {role === "participant"
                  ? (lang==="id"
                      ? "Belum ada dokumen di Knowledge Base kursus ini, jadi asisten belum bisa menjawab dari materi. Hubungi trainer Anda."
                      : "No documents in this course's Knowledge Base yet, so the assistant has nothing to answer from. Contact your trainer.")
                  : (lang==="id"
                      ? "Belum ada dokumen di Knowledge Base. Unggah dokumen lebih dulu agar asisten punya sumber jawaban."
                      : "No documents in the Knowledge Base yet. Upload documents first so the assistant has something to answer from.")}
                {role !== "participant" && (
                  <button className="tag outline" style={{cursor:"pointer", fontSize:12, marginLeft:8}}
                    onClick={()=>navigate("kb")}>
                    {lang==="id"?"Buka Knowledge Base":"Open Knowledge Base"}
                  </button>
                )}
              </div>
            ) : (
              <div className="row gap-2 mb-2" style={{flexWrap:"wrap"}}>
                <span className="tiny muted" style={{padding:"4px 0"}}>{t.suggestedQuestions}:</span>
                {suggested.map((s, i) => (
                  <button key={i} className="tag outline" style={{cursor:"pointer", fontSize:12}} onClick={()=>setInput(s)}>{s}</button>
                ))}
              </div>
            )}
            <div className="row gap-2" style={{background:"var(--surface)", border:"1px solid var(--border)", padding:"4px", borderRadius:"var(--r-md)"}}>
              <Btn variant="ghost" size="sm" icon={<Icon name="paperclip" size={14}/>}/>
              <Btn variant="ghost" size="sm" icon={<Icon name="mic" size={14}/>}/>
              <textarea
                placeholder={t.askAnything}
                value={input}
                onChange={(e)=>setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
                style={{flex:1, border:0, outline:"none", padding:"8px", resize:"none", minHeight: 40, fontFamily:"inherit", background:"transparent"}}
              />
              <Btn variant="primary" size="sm" icon={<Icon name="send" size={14}/>} onClick={handleSend} disabled={sending || !input.trim()}/>
            </div>
            {/* Baris bawah dihapus: berisi kalimat penafian panjang plus nama
                model penyedia. Keduanya jejak teknis, dan yang penting darinya
                sudah disampaikan di tempat yang lebih tepat — spanduk "Tidak
                ditemukan di KB" muncul persis saat AI memang tidak punya dasar
                menjawab, alih-alih peringatan permanen yang berhenti dibaca. */}
          </div>
        </div>

        {/* Context panel — hidden in compact mode */}
        {!compact && <div className="col gap-3">
          <div className="tile">
            <div className="tile-header">
              <h3>{lang==="id"?"Knowledge Base aktif":"Active Knowledge Base"}</h3>
              <a href="#" className="tile-header-sub" onClick={(e)=>{e.preventDefault();navigate("kb");}}>{lang==="id"?"Kelola":"Manage"}</a>
            </div>
            <div style={{padding: "12px 16px"}}>
              {/* Was a fixed list of one institution's maritime documents,
                  presented as if it were the reader's own knowledge base.
                  Every institution saw the same five filenames. */}
              {currentProgram?.name && (
                <div className="row gap-2 mb-3">
                  {currentProgram.level ? <LevelBadge level={currentProgram.level}/> : null}
                  <span className="small">{currentProgram.name}</span>
                </div>
              )}
              <div className="tiny muted mb-2">{lang==="id"?"Dokumen yang diindex untuk kursus ini":"Documents indexed for this course"}</div>
              <div className="col gap-2">
                {kbDocs === null ? (
                  <span className="tiny muted">{lang==="id"?"Memuat…":"Loading…"}</span>
                ) : kbDocs.length === 0 ? (
                  <span className="tiny muted">{lang==="id"?"Belum ada dokumen diunggah.":"No documents uploaded yet."}</span>
                ) : kbDocs.map((s)=>(
                  <div key={s.id} className="row gap-2"><Icon name="doc" size={11} color="var(--blue-70)"/><span className="tiny mono text-truncate" style={{flex:1}}>{s.filename}</span><span className="tiny muted">{s._count?.chunks ?? 0}</span></div>
                ))}
              </div>
              <div className="divider"/>
              <div className="tiny muted row between"><span>{lang==="id"?"Total chunk":"Total chunks"}</span><span className="mono">{kbDocs ? kbDocs.reduce((n,d)=>n+(d._count?.chunks||0),0) : "—"}</span></div>
            </div>
          </div>

          <div className="tile">
            <div className="tile-header"><h3>{lang==="id"?"Mode jawaban":"Answer mode"}</h3></div>
            <div className="col gap-2" style={{padding: 12}}>
              {ANSWER_MODES.map((m) => {
                const active = m.key === mode
                return (
                  <label key={m.key} className="row gap-2" style={{padding:"6px 8px", background: active?"var(--blue-10)":"transparent", cursor:"pointer", border:"1px solid", borderColor: active?"var(--blue-60)":"var(--border)"}}>
                    <input
                      type="radio"
                      name="mode"
                      value={m.key}
                      checked={active}
                      onChange={() => setMode(m.key)}
                      style={{accentColor:"var(--blue-60)"}}
                    />
                    <span className="small">{lang==="id" ? m.id : m.en}</span>
                  </label>
                )
              })}
            </div>
          </div>

          <div className="tile" style={{background:"var(--amber-10)", borderColor:"var(--amber-50)"}}>
            <div style={{padding: 12}}>
              <div className="row gap-2 mb-2"><Icon name="alert" size={14} color="var(--amber-50)"/><strong className="small">{lang==="id"?"Hanya jawab dari dokumen":"Document-only answers"}</strong></div>
              <p className="tiny" style={{margin:0, color:"var(--amber-ink, #6a4f00)", lineHeight:1.5}}>
                {lang==="id"
                  ? "Asisten ini hanya menjawab dari dokumen yang diupload ke Knowledge Base course ini. Tidak ada akses internet atau general knowledge."
                  : "This assistant only answers from documents uploaded to this course's Knowledge Base. No internet or general knowledge."}
              </p>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
}

// ── Floating side panel for participants ─────────────────────
export function AIFloatingPanel({ open, onToggle }) {
  const { lang } = useApp()

  return (
    <>
      {/* Vertical tab trigger on right edge */}
      <button
        onClick={onToggle}
        data-nav="ai"
        style={{
          position: "fixed",
          right: open ? 400 : 0,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1100,
          background: "var(--program-color, #B84E2A)",
          color: "white",
          border: "none",
          borderRadius: "8px 0 0 8px",
          padding: "12px 8px",
          cursor: "pointer",
          writingMode: "vertical-lr",
          textOrientation: "mixed",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.04em",
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "right 0.3s ease",
          boxShadow: "-2px 0 12px rgba(0,0,0,0.15)",
          whiteSpace: "nowrap",
        }}
        title="AI Assistant"
      >
        <Icon name="ai" size={14} color="white" style={{ transform: "rotate(90deg)" }} />
        AI Assistant
      </button>

      {/* Slide-in panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: open ? 0 : -400,
          width: 400,
          height: "100vh",
          background: "var(--surface, white)",
          borderLeft: "1px solid var(--border)",
          boxShadow: open ? "-4px 0 24px rgba(0,0,0,0.12)" : "none",
          zIndex: 1099,
          display: "flex",
          flexDirection: "column",
          transition: "right 0.3s ease",
          overflow: "hidden",
        }}
      >
        {/* Panel header */}
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, background: "var(--program-color, #B84E2A)" }}>
          <Icon name="ai" size={18} color="white" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "white" }}>AI Assistant</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)" }}>{lang === "id" ? "Tanya tentang materi kursus" : "Ask about course materials"}</div>
          </div>
          <button onClick={onToggle} style={{ background: "none", border: "none", color: "white", cursor: "pointer", padding: 4, borderRadius: 4, display: "grid", placeItems: "center" }}>
            <Icon name="close" size={16} color="white" />
          </button>
        </div>
        {/* Chat content */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {open && <AIChatScreen compact />}
        </div>
      </div>

      {/* Backdrop */}
      {open && (
        <div
          onClick={onToggle}
          style={{ position: "fixed", inset: 0, zIndex: 1098, background: "rgba(0,0,0,0.2)" }}
        />
      )}
    </>
  )
}

export default AIChatScreen
